import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, TextField, MenuItem, Button, Stack, Chip, Alert,
  Table, TableHead, TableBody, TableRow, TableCell, Accordion, AccordionSummary,
  AccordionDetails, CircularProgress, IconButton, Collapse, Divider, Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import api from '../../services/api';

interface Change { serviceItemName?: string; field?: string; from?: any; to?: any; }
interface AuditEntry {
  _id: string; runId: string; source: 'bulk' | 'single'; labName: string;
  runByName?: string; runAt: string; reportId?: string; orderId?: string;
  patientName?: string; status: 'applied' | 'skipped' | 'error'; detail?: string;
  totalChanges?: number; namesReresolved?: number; referenceFilled?: number;
  duplicatesRemoved?: number; changes?: Change[];
}
interface Run {
  runId: string; source: string; labName: string; runByName?: string; runAt: string;
  entries: AuditEntry[]; applied: number; skipped: number; error: number;
}

const fmtDateTime = (d: string) => (d ? new Date(d).toLocaleString() : '—');
const val = (v: any) => (v === undefined || v === null || v === '' ? '∅' : typeof v === 'object' ? JSON.stringify(v) : String(v));

const StatusChip: React.FC<{ s: string }> = ({ s }) => (
  s === 'applied' ? <Chip size="small" color="success" variant="outlined" label="applied" />
    : s === 'skipped' ? <Chip size="small" color="warning" label="skipped" />
      : <Chip size="small" color="error" variant="outlined" label="error" />
);

const ReportRow: React.FC<{ e: AuditEntry }> = ({ e }) => {
  const [open, setOpen] = useState(false);
  const hasChanges = (e.changes?.length || 0) > 0;
  return (
    <>
      <TableRow hover>
        <TableCell padding="checkbox">
          <IconButton size="small" disabled={!hasChanges} onClick={() => setOpen((o) => !o)}>
            {open ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell>{e.patientName || '—'}</TableCell>
        <TableCell><code>{e.orderId || '—'}</code></TableCell>
        <TableCell><StatusChip s={e.status} /></TableCell>
        <TableCell>
          {e.status === 'applied'
            ? [
                e.namesReresolved ? `${e.namesReresolved} name(s) re-resolved` : '',
                e.referenceFilled ? `${e.referenceFilled} reference filled` : '',
                e.duplicatesRemoved ? `${e.duplicatesRemoved} duplicate(s) removed` : '',
              ].filter(Boolean).join(' · ') || 'no changes'
            : <Typography variant="caption" color="text.secondary">{e.detail || ''}</Typography>}
        </TableCell>
      </TableRow>
      {hasChanges && (
        <TableRow>
          <TableCell colSpan={5} sx={{ py: 0, border: 0 }}>
            <Collapse in={open} unmountOnExit>
              <Box sx={{ m: 1, ml: 5 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Parameter</TableCell>
                      <TableCell>Field</TableCell>
                      <TableCell>From</TableCell>
                      <TableCell>To</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {e.changes!.map((c, i) => (
                      <TableRow key={i}>
                        <TableCell>{c.serviceItemName}</TableCell>
                        <TableCell><code>{c.field}</code></TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{val(c.from)}</TableCell>
                        <TableCell sx={{ color: 'success.main', fontWeight: 600 }}>{val(c.to)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const ResendLog: React.FC<{ embedded?: boolean }> = ({ embedded }) => {
  const [labs, setLabs] = useState<{ labName: string; count: number }[]>([]);
  const [labName, setLabName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);

  useEffect(() => {
    api.get('/review/bulk-republish/labs').then((r) => setLabs(r.data?.labs || [])).catch(() => setLabs([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/review/bulk-republish/audit', {
        params: { labName: labName || undefined, startDate: startDate || undefined, endDate: endDate || undefined },
      });
      setEntries(res.data?.entries || []);
    } catch {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [labName, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const runs: Run[] = useMemo(() => {
    const map = new Map<string, Run>();
    for (const e of entries || []) {
      let r = map.get(e.runId);
      if (!r) { r = { runId: e.runId, source: e.source, labName: e.labName, runByName: e.runByName, runAt: e.runAt, entries: [], applied: 0, skipped: 0, error: 0 }; map.set(e.runId, r); }
      r.entries.push(e);
      (r as any)[e.status] = ((r as any)[e.status] || 0) + 1;
    }
    return Array.from(map.values());
  }, [entries]);

  return (
    <Box sx={{ p: embedded ? 0 : 3, maxWidth: 1200, mx: 'auto' }}>
      {!embedded && <Typography variant="h5" fontWeight={700} gutterBottom>Re-send Log</Typography>}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Every Fix &amp; Re-send that changed the model database — lab-wise, per report, field by field.
        (These changes are not visible on the report review screen; this is the record of them.)
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-end">
          <TextField select label="Lab" value={labName} onChange={(e) => setLabName(e.target.value)} sx={{ minWidth: 220 }} size="small">
            <MenuItem value="">All labs</MenuItem>
            {labs.map((l) => <MenuItem key={l.labName} value={l.labName}>{l.labName}</MenuItem>)}
          </TextField>
          <TextField label="From" type="date" size="small" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="To" type="date" size="small" value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          <Button variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
        </Stack>
      </Paper>

      {entries && entries.length === 0 && <Alert severity="info">No re-send activity for this filter yet.</Alert>}

      {runs.map((run) => (
        <Accordion key={run.runId} defaultExpanded={runs.length <= 3}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ width: '100%', pr: 2 }}>
              <Chip size="small" label={run.source} variant="outlined" />
              <Typography fontWeight={600}>{run.labName || '—'}</Typography>
              <Typography variant="body2" color="text.secondary">{fmtDateTime(run.runAt)}</Typography>
              {run.runByName ? <Typography variant="body2" color="text.secondary">· {run.runByName}</Typography> : null}
              <Box flexGrow={1} />
              <Chip size="small" color="success" variant="outlined" label={`${run.applied} applied`} />
              {run.skipped > 0 && <Chip size="small" color="warning" label={`${run.skipped} skipped`} />}
              {run.error > 0 && <Chip size="small" color="error" variant="outlined" label={`${run.error} error`} />}
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <Divider />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Patient</TableCell>
                  <TableCell>Order ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>What changed</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {run.entries.map((e) => <ReportRow key={e._id} e={e} />)}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default ResendLog;
